import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Grid, IconButton,
  Divider, Typography, Box, CircularProgress
} from "@mui/material";
import { Close as CloseIcon, Business as BusinessIcon } from "@mui/icons-material";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

// Constantes para los tipos disponibles
const TIPOS_ORGANIZACION = {
  PROVEEDOR: "PROVEEDOR",
  PROPIA: "PROPIA",
  CLIENTE: "CLIENTE"
};

const TIPOS_OPTIONS = [
  { value: TIPOS_ORGANIZACION.PROVEEDOR, label: "Proveedor" },
  { value: TIPOS_ORGANIZACION.PROPIA, label: "Propia" },
  { value: TIPOS_ORGANIZACION.CLIENTE, label: "Cliente" }
];

// Esquema de validación mejorado
const schema = yup.object().shape({
  orgnom: yup.string()
    .required("El nombre comercial es obligatorio")
    .max(100, "Máximo 100 caracteres"),
  orgnomsoc: yup.string()
    .required("La razón social es obligatoria")
    .max(150, "Máximo 150 caracteres"),
  orgruc: yup.string()
    .max(20, "Máximo 20 caracteres")
    .matches(/^[0-9]*$/, "Solo números")
    .nullable()
    .transform(value => value || null),
  orgtip: yup.string()
    .oneOf(Object.values(TIPOS_ORGANIZACION), "Tipo de organización inválido")
    .required("El tipo de organización es obligatorio"),
  orgtel: yup.string()
    .max(32, "Máximo 32 caracteres")
    .nullable()
    .transform(value => value || null),
  orgtel2: yup.string()
    .max(32, "Máximo 32 caracteres")
    .nullable()
    .transform(value => value || null),
  orgema: yup.string()
    .email("Correo electrónico inválido")
    .max(100, "Máximo 100 caracteres")
    .nullable()
    .transform(value => value || null),
  orgema2: yup.string()
    .email("Correo electrónico inválido")
    .max(100, "Máximo 100 caracteres")
    .nullable()
    .transform(value => value || null),
  orgdir: yup.string()
    .max(255, "Máximo 255 caracteres")
    .nullable()
    .transform(value => value || null),
});

// Función para limpiar valores inválidos
const getValidOrgTip = (value) => {
  if (!value) return TIPOS_ORGANIZACION.PROVEEDOR;
  if (Object.values(TIPOS_ORGANIZACION).includes(value)) return value;
  return TIPOS_ORGANIZACION.PROVEEDOR;
};

const OrganizationProvForm = ({ open, handleClose, onSubmit, defaultValues = {} }) => {
  const isEditing = Boolean(defaultValues?.orgid);

  // Preparar valores por defecto seguros
  const safeDefaultValues = {
    orgnom: defaultValues.orgnom || "",
    orgnomsoc: defaultValues.orgnomsoc || "",
    orgruc: defaultValues.orgruc || "",
    orgtip: getValidOrgTip(defaultValues.orgtip),
    orgtel: defaultValues.orgtel || "",
    orgtel2: defaultValues.orgtel2 || "",
    orgema: defaultValues.orgema || "",
    orgema2: defaultValues.orgema2 || "",
    orgdir: defaultValues.orgdir || "",
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    reset,
    setValue,
    watch
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: safeDefaultValues,
    mode: "onChange"
  });

  const watchTipo = watch("orgtip");

  // Resetear formulario cuando se abre
  useEffect(() => {
    if (open) {
      const resetValues = {
        orgnom: defaultValues.orgnom || "",
        orgnomsoc: defaultValues.orgnomsoc || "",
        orgruc: defaultValues.orgruc || "",
        orgtip: getValidOrgTip(defaultValues.orgtip),
        orgtel: defaultValues.orgtel || "",
        orgtel2: defaultValues.orgtel2 || "",
        orgema: defaultValues.orgema || "",
        orgema2: defaultValues.orgema2 || "",
        orgdir: defaultValues.orgdir || "",
      };
      reset(resetValues);
    }
  }, [open, defaultValues, reset]);

  const handleFormSubmit = async (data) => {
    try {
      await onSubmit(data);
      // El padre maneja el cierre
    } catch (error) {
      console.error("Error al enviar formulario:", error);
    }
  };

  const handleCancel = () => {
    reset();
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{
        background:
        'linear-gradient(135deg, #0F172A 0%, #1E3A8A 55%, #2563EB 100%)',
        color: "white",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
        <Box display="flex" alignItems="center" gap={1}>
          <BusinessIcon />
          <Typography variant="h6">
            {isEditing ? "Editar Organización" : "Crear Nueva Organización"}
          </Typography>
        </Box>
        <IconButton onClick={handleCancel} size="small" sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {/* Información principal */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Información Principal
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Nombre Comercial"
                fullWidth
                size="small"
                {...register("orgnom")}
                error={!!errors.orgnom}
                helperText={errors.orgnom?.message}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Razón Social"
                fullWidth
                size="small"
                {...register("orgnomsoc")}
                error={!!errors.orgnomsoc}
                helperText={errors.orgnomsoc?.message}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="RUC"
                fullWidth
                size="small"
                {...register("orgruc")}
                error={!!errors.orgruc}
                helperText={errors.orgruc?.message}
                placeholder="Solo números"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Tipo de Organización"
                fullWidth
                size="small"
                value={watchTipo || TIPOS_ORGANIZACION.PROVEEDOR}
                onChange={(e) => setValue("orgtip", e.target.value, { shouldValidate: true })}
                error={!!errors.orgtip}
                helperText={errors.orgtip?.message}
                required
              >
                {TIPOS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Información de contacto */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
                Información de Contacto
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Teléfono Principal"
                fullWidth
                size="small"
                {...register("orgtel")}
                error={!!errors.orgtel}
                helperText={errors.orgtel?.message}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Teléfono Secundario"
                fullWidth
                size="small"
                {...register("orgtel2")}
                error={!!errors.orgtel2}
                helperText={errors.orgtel2?.message}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Correo Electrónico"
                fullWidth
                size="small"
                type="email"
                {...register("orgema")}
                error={!!errors.orgema}
                helperText={errors.orgema?.message}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Correo Electrónico Secundario"
                fullWidth
                size="small"
                type="email"
                {...register("orgema2")}
                error={!!errors.orgema2}
                helperText={errors.orgema2?.message}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Dirección"
                fullWidth
                size="small"
                multiline
                rows={2}
                {...register("orgdir")}
                error={!!errors.orgdir}
                helperText={errors.orgdir?.message}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2, bgcolor: "grey.50" }}>
          <Button
            onClick={handleCancel}
            variant="outlined"
            color="inherit"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting || !isValid}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting
              ? (isEditing ? "Actualizando..." : "Guardando...")
              : (isEditing ? "Actualizar" : "Guardar")
            }
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default OrganizationProvForm;